import React, { useEffect, useRef, useState } from "react"
import "./CommonCourseList.scss"
import CommonCourseApi from "Api/CommonCourseApi"
import {
  Button,
  Col,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
  Table,
  Spinner,
  Input,
} from "reactstrap"
import CustomBreadCrumb from "utils/CustomBreadCrumb/CustomBreadCrumb"
import { useNavigate } from "react-router-dom"
import DataTableLoader from "SkeletonLoader/DataTableLoader"
import { Toast } from 'primereact/toast'
import { OverlayPanel } from "primereact/overlaypanel"

const CommonCourseList = () => {
  document.title = "Common Courses"

  const accessToken = localStorage.getItem("accessToken")
  const [commonCourses, setCommonCourses] = useState([])
  const [page, setPage] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)
  const [limit, setLimit] = useState(10)
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const toast = useRef(null)
  const os = useRef(null)
  const [selectedCourses, setSelectedCourses] = useState([])
  const [filterStatus, setFilterStatus] = useState("all")
  const [FilteredCourseDetails, setFilteredCourseDetails] = useState([])

  const getAllCommonCourses = () => {
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      params: {
        limit: limit,
        page: page,
      },
    }

    CommonCourseApi.getCommonCourse(config)
      .then(response => {
        if (response.data && response.data.status) {
          setLoading(false)
          setCommonCourses(response.data.data?.courses || [])
          setTotalRecords(response.data.data?.pagination?.totalRecords || 0)
        } else {
          setLoading(false)
          toast.current.show({
            severity: 'error',
            summary: 'Error',
            detail: response.data?.message || 'Invalid response format',
            life: 3000
          })
        }
      })
      .catch(error => {
        setLoading(false)
        console.error("Error fetching common courses:", error)
        toast.current.show({
          severity: 'error',
          summary: 'Error',
          detail: error.response?.data?.message || 'Failed to fetch common courses',
          life: 3000
        })
      })
  }

  useEffect(() => {
    getAllCommonCourses()
  }, [accessToken, page, limit])

  useEffect(() => {
    let filteredData = commonCourses

    if (filterStatus !== "all") {
      filteredData = filteredData.filter(
        course => course.verifiedStatus === filterStatus
      )
    }

    setFilteredCourseDetails(filteredData)
  }, [filterStatus, commonCourses])

  const breadcrumbItems = [
    { label: "Pixwik", link: "#" },
    { label: "Common Courses", link: "#" },
    { label: "Common Course List", link: "#" },
  ]

  const totalPages = Math.ceil(totalRecords / limit)

  const toggleSelectAll = () => {
    if (selectedCourses.length === FilteredCourseDetails.length) {
      setSelectedCourses([])
    } else {
      setSelectedCourses(FilteredCourseDetails.map(course => course._id))
    }
  }

  const toggleSelection = courseId => {
    setSelectedCourses(prevCourses =>
      prevCourses.includes(courseId)
        ? prevCourses.filter(id => id !== courseId)
        : [...prevCourses, courseId],
    )
  }

  const handleUpdateStatus = (status) => {
    if (selectedCourses.length === 0) {
      toast.current.show({severity:'warn', summary: 'Warning', detail: 'Please select at least one Course to update.', life: 3000});
      return
    }

    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }

    const payload = {
      courseIds: selectedCourses,
      newStatus: status
    }

    CommonCourseApi.updateCommonCourseStatus(payload, config)
      .then(response => {
        if (response.data.status) {
          toast.current.show({
            severity: 'success',
            summary: 'Success',
            detail: response.data.message,
            life: 3000
          })
          getAllCommonCourses()
          setSelectedCourses([])
        }
      })
      .catch(error => {
        toast.current.show({
          severity: 'error',
          summary: 'Error',
          detail: error.response?.data?.message || 'Failed to update courses status',
          life: 3000
        })
      })
  }

  const handleDeleteCourse = (courseId) => {
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
    
    CommonCourseApi.deleteCommonCourse(courseId, config)
      .then(response => {
        if (response.data.status) {
          toast.current.show({severity:'success', summary: 'Success', detail: response.data.message, life: 3000});
          getAllCommonCourses()
        }
      })
      .catch(error => {
        toast.current.show({severity:'error', summary: 'Error', detail: error.response?.data?.message || 'Failed to delete course', life: 3000});
      })
  }

  return (
    <>
      <Toast ref={toast} />
      <Row>
        <Col xs="12">
          <div className="common-course-list-header d-flex align-items-center justify-content-between">
            <CustomBreadCrumb data={breadcrumbItems} pageTitle="Common Courses" />
            <div className="d-flex align-items-center gap-3">
              {selectedCourses.length === 0 && (
                <Button
                  type="button"
                  color="secondary"
                  className="waves-effect"
                  onClick={() => navigate("/addCommonCourse")}
                >
                  Add Common Course
                </Button>
              )}

              {selectedCourses.length > 0 && (
                <>
                  <Button
                    type="button"
                    color="primary"
                    className="waves-effect"
                    onClick={() => handleUpdateStatus("approve")}
                  >
                    Approve
                  </Button>
                  <Button
                    type="button"
                    color="danger"
                    className="waves-effect"
                    onClick={() => handleUpdateStatus("reject")}
                  >
                    Reject
                  </Button>
                </>
              )}
            </div>
          </div>

          <div className="common-course-data-table table-responsive mt-4 mb-3">
            {loading ? (
              <DataTableLoader cols={7} />
            ) : (
              <Table className="table mb-0">
                <thead className="table-light">
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        onClick={toggleSelectAll}
                        checked={
                          selectedCourses.length ===
                          FilteredCourseDetails.length
                        }
                      />
                    </th>
                    <th>#</th>
                    <th>Course Name</th>
                    <th>Overview</th>
                    <th>Career Outcomes</th>
                    <th>Exams</th>
                    <th>
                      <span className="d-flex align-items-center gap-2 justify-content-center">
                        <span>Status</span>
                        <span
                          onClick={e => os.current.toggle(e)}
                          style={{ cursor: "pointer" }}
                        >
                          <i className="mdi mdi-filter" />
                        </span>
                      </span>
                      <OverlayPanel ref={os}>
                        <Input
                          type="select"
                          value={filterStatus}
                          onChange={e => {
                            setFilterStatus(e.target.value)
                            os.current.hide()
                          }}
                          style={{ width: "150px" }}
                        >
                          <option value="all">All</option>
                          <option value="approve">Approved</option>
                          <option value="reject">Rejected</option>
                          <option value="pending">Pending</option>
                        </Input>
                      </OverlayPanel>
                    </th>
                    <th>Edit</th>
                    <th>View</th>
                    <th>Delete</th>
                  </tr>
                </thead>
                <tbody>
                  {FilteredCourseDetails.map((course, index) => (
                    <tr key={course._id} className="university-table-row">
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedCourses.includes(course._id)}
                          onClick={() => {
                            toggleSelection(course._id)
                          }}
                        />
                      </td>
                      <th scope="row">{(page - 1) * limit + index + 1}</th>
                      <td>{course.courseName}</td>
                      <td>{course.courseOverview?.overview?.substring(0, 50)}...</td>
                      <td>{course.courseCareerOutcome?.careerOutcome?.substring(0, 50)}...</td>
                      <td>{course.courseExam?.length || 0} Exams</td>
                      <td>
                        <span
                          className={`status ${course.verifiedStatus?.toLowerCase() || 'pending'}`}
                        >
                          {course.verifiedStatus === "approve"
                            ? "Approved"
                            : course.verifiedStatus === "reject"
                              ? "Rejected"
                              : "Pending"}
                        </span>
                      </td>
                      <td>
                        <span
                          className="edit-course"
                          onClick={() => {
                            navigate(`/editCommonCourse/${course._id}`, {
                              state: course,
                            })
                          }}
                        >
                          <i className="ion ion-md-create" />
                        </span>
                      </td>
                      <td>
                        <span
                          className="view-course"
                          onClick={() => {
                            navigate(`/viewCommonCourse/${course._id}`)
                          }}
                        >
                          <i className="ion ion-md-eye" />
                        </span>
                      </td>
                      <td>
                        <span
                          className="delete-course"
                          onClick={() => handleDeleteCourse(course._id)}
                        >
                          <i className="ion ion-md-trash" />
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>

          <div className="pagination-container d-flex align-items-center justify-content-end">
            <Pagination>
              <PaginationItem disabled={page <= 1}>
                <PaginationLink previous onClick={() => setPage(page - 1)} />
              </PaginationItem>
              {[...Array(Math.min(3, totalPages))].map((_, index) => (
                <PaginationItem key={index} active={index + 1 === page}>
                  <PaginationLink onClick={() => setPage(index + 1)}>
                    {index + 1}
                  </PaginationLink>
                </PaginationItem>
              ))}

              {totalPages > 6 && page > 3 && page < totalPages - 2 && (
                <PaginationItem disabled>
                  <PaginationLink>...</PaginationLink>
                </PaginationItem>
              )}

              {totalPages > 6 && page > 3 && page < totalPages - 2 && (
                <PaginationItem active>
                  <PaginationLink onClick={() => setPage(page)}>
                    {page}
                  </PaginationLink>
                </PaginationItem>
              )}

              {totalPages > 6 && page < totalPages - 2 && (
                <PaginationItem disabled>
                  <PaginationLink>...</PaginationLink>
                </PaginationItem>
              )}

              {totalPages > 6 &&
                [...Array(Math.min(3, totalPages))].map((_, index) => {
                  const pageNumber = totalPages - 2 + index
                  return (
                    <PaginationItem
                      key={pageNumber}
                      active={pageNumber === page}
                    >
                      <PaginationLink onClick={() => setPage(pageNumber)}>
                        {pageNumber}
                      </PaginationLink>
                    </PaginationItem>
                  )
                })}
              <PaginationItem disabled={page >= totalPages}>
                <PaginationLink next onClick={() => setPage(page + 1)} />
              </PaginationItem>
            </Pagination>
          </div>
        </Col>
      </Row>
    </>
  )
}

export default CommonCourseList
